import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getSubmissions } from '../../../../store/tasklists/selectors';
import { getSelectedFilterItems } from '../../../../store/common/selectors';
import { TEmptySubmission } from '../../../../types';

export function useFilterSubmissions() {
  const submissions = useAppSelector(getSubmissions);
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const { t } = useTranslation(['inputs', 'tasklists', 'timesheets']);

  const filterByStatus = (submissions: { [key: string]: Array<TEmptySubmission> }) => {
    if (selectedFilterItems.status.length > 1) {
      return submissions;
    }

    const result: { [key: string]: Array<TEmptySubmission> } = {};

    Object.entries(submissions).forEach(([date, tasklists]) => {
      const filteredTasklists = tasklists.filter((tasklist) => {
        const isDone = tasklist.tasks.every((task) => task.doneBy.length > 0);

        return (selectedFilterItems.status[0].value === 'done' && isDone)
          || (selectedFilterItems.status[0].value === 'inProgress' && !isDone);
      });

      if (filteredTasklists.length > 0) {
        result[date] = filteredTasklists;
      }
    });

    return result;
  };

  const filterDataByDates = (submissions: { [key: string]: Array<TEmptySubmission> }) => {
    const result: { [key: string]: Array<TEmptySubmission> } = {};

    selectedFilterItems.date.forEach((dateObj) => {
      const date = dateObj.value as string;
      if (submissions[date]) {
        // If the date exists in the data object, add it to the result
        result[date] = submissions[date];
      }
    });

    return result;
  };

  const filterSubmissionItems = useMemo(() => [
    {
      value: 'status',
      selectedKey: 'status',
      name: 'Status',
      children: [
        {
          name: t('done', { ns: 'tasklists' }),
          value: 'done',
        },
        {
          name: t('inProgress', { ns: 'tasklists' }),
          value: 'inProgress',
        },
      ],
      isSelected: false,
    },
    {
      value: 'date',
      selectedKey: 'date',
      name: t('date', { ns: 'timesheets' }),
      children: Object.keys(submissions).map((val) => ({
        name: val,
        value: val,
      })),
      isSelected: false,
    },
  ], [submissions]);

  const getSubmissionsByFilters = useMemo(() => {
    let filteredSubmissions = submissions;

    if (selectedFilterItems?.status?.length) {
      filteredSubmissions = filterByStatus(filteredSubmissions);
    }
    if (selectedFilterItems?.date?.length) {
      filteredSubmissions = filterDataByDates(filteredSubmissions);
    }

    return filteredSubmissions;
  }, [submissions, selectedFilterItems]);

  return {
    filterSubmissionItems,
    getSubmissionsByFilters,
  };
}
